/**
 * Static data for the pricing page.
 *
 * Extracted from pricing-page.tsx to keep the page component lean.
 */

import type { PlanDurations } from "@/features/subscription/types";

// Cost threshold: subscribers at or above this monthly cost see contact-us flow instead of self-serve cards
export const HIGH_VALUE_COST_THRESHOLD = 50;
export const HIGH_VALUE_YEARLY_THRESHOLD = 600;

// Per-use rates displayed on the cards and FAQ — MIRRORS of the server's
// charging code, which is ground truth. Every number derives from
// speak-server src/@speak-payment/config/creditPricing.ts:
//   base transcription 1 credit/hr (:8), premium-engine +0.5/hr (:9),
//   meeting +1/hr (:10), translation 6 credits/100K chars (:11),
//   Pay-As-You-Go pays 2x on everything (:64). 1 credit = $1.
// AI Chat is priced per turn at provider cost / (1 - 0.7 margin) (:83-84);
// the "from $0.08/chat" PAYG figure is the cheapest model's measured
// production average from chatCredits.test.ts (:9-14) at that margin.
// Update here whenever the server constants move.
export const SUBSCRIPTION_TRANSCRIPTION_RATE_PER_HOUR = 1.0;
export const PAYG_MULTIPLIER = 2;
export const PAYG_TRANSCRIPTION_RATE_PER_HOUR =
  SUBSCRIPTION_TRANSCRIPTION_RATE_PER_HOUR * PAYG_MULTIPLIER;

// Rate row type used by pricing cards
export interface PricingCardRate {
  label: string;
  /** i18n key for the label; plain `label` is the fallback (and legacy static rows). */
  labelKey?: string;
  value: string;
  tooltip?: string;
  tooltipKey?: string;
}

/**
 * Checkmark-list entry on a pricing card. Bare strings are legacy static copy;
 * keyed entries translate through i18n; `isNew` renders the compact NEW badge.
 * Every entry must fit ONE line at the card's normal width (~38 chars).
 */
export interface PricingFeature {
  key?: string;
  text: string;
  isNew?: boolean;
}
export type PricingFeatureEntry = string | PricingFeature;

// -- Duration plans (only monthly + yearly for v4) -----------------

export const DURATION_MONTHLY: PlanDurations = {
  id: "monthly",
  value: "Monthly",
  months: 1,
  discountValue: 0,
  label: "Monthly",
  rawLabel: "Month",
};

export const DURATION_YEARLY: PlanDurations = {
  id: "yearly",
  value: "Yearly",
  months: 12,
  discountValue: 20,
  label: "Per Year",
  rawLabel: "Year",
};

// -- FAQ data ------------------------------------------------------

export interface FaqItem {
  q: string;
  a: string;
  link?: { label: string; href: string };
}

export const FAQ_ITEMS: FaqItem[] = [
  {
    q: "What happens when I exceed my included hours?",
    a: `You're never cut off. Once your included credits are used, usage draws from your wallet at per-use rates — standard-language transcription is $${SUBSCRIPTION_TRANSCRIPTION_RATE_PER_HOUR.toFixed(2)}/hr on subscription plans. If your balance is insufficient, your card on file is charged ($5 minimum). If the charge exceeds your actual usage, the difference is credited to your wallet.`,
  },
  {
    q: "Can I try Pro before committing?",
    a: "Yes, Pay As You Go lets you test transcription and AI Chat with no card and no commitment. When you're ready to keep every recording searchable forever, AI Chat at scale, and invite your team, upgrade to Pro. Downgrade or cancel anytime.",
  },
  {
    q: "Can I add team members?",
    a: "Pro supports up to 5 users, each with their own included hours and storage. See the Pro card above for current per-seat pricing. Need more than 5? Contact us for Custom pricing.",
  },
  {
    q: "How does AI Chat billing work?",
    a: "AI Chat is priced per chat based on the model you pick — from about $0.04 per chat with Gemini Flash on subscription plans (Pay As You Go pays double). Your monthly credits cover chats automatically; beyond them, usage draws from your wallet. We route each task to the best model for the job.",
  },
  {
    q: "How does wallet billing work?",
    a: "Your wallet balance covers usage charges automatically. When your balance runs low, we charge your card on file with a $5 minimum. Any excess is credited for future use. You can add balance manually or enable auto-reload to stay topped up.",
  },
  {
    q: "Can I switch plans anytime?",
    a: "Yes. Upgrade, downgrade, or pause anytime. When you upgrade from Pay As You Go to Pro, your existing files immediately get permanent retention. When you downgrade, your data is preserved for 30 days.",
  },
  {
    q: "Is there a usage limit?",
    a: "No hard limits beyond your included hours. We reserve the right to review accounts with usage consistently and substantially above plan norms and work with you to find the right plan. We will always contact you before making any changes to your account.",
  },
  {
    q: "Is my data secure?",
    a: "Yes. SSO/SAML is available on all plans. Custom plans include BAA, NDA, data residency options, and custom compliance agreements. We never train AI models on your data.",
    link: {
      label: "Read more about security & privacy",
      href: "https://docs.speakai.co/help/security/policies/",
    },
  },
  {
    q: "Do you offer translation?",
    a: "Yes. AI text translation is available on Pro and Custom plans, letting you translate transcripts into 50+ languages. Voice translation is available on Custom plans. Translation is billed separately from transcription.",
  },
];

// -- Testimonials --------------------------------------------------

export const TESTIMONIALS = [
  {
    quote:
      "We went from weeks of qual analysis to one day. Easy to use, easy to implement, and the support has been incredible.",
    name: "Connor H.",
    role: "Data and Impact Analyst, British Triathlon Federation",
  },
  {
    quote:
      "I use Speak in French and English for meetings up to two hours. It saves time and increases the precision of my reports.",
    name: "Francois L.",
    role: "Financial Advisor",
  },
  {
    quote:
      "It transformed how I handle transcription, subtitling, and translation. The summaries are concise and accurate.",
    name: "Maria S.",
    role: "Freelance Linguist",
  },
];

// -- Use cases -----------------------------------------------------

export const USE_CASES = [
  {
    name: "Qualitative Research",
    desc: "Analyze interviews, focus groups, and customer calls at scale with AI themes and sentiment.",
  },
  {
    name: "Meeting Intelligence",
    desc: "Capture, transcribe, and get insights from every meeting automatically.",
  },
  {
    name: "Team Collaboration",
    desc: "Share transcripts, folders, and insights across your organization.",
  },
  {
    name: "Multilingual Transcription",
    desc: "Transcribe and analyze in 100+ languages with automatic translation.",
  },
];

// -- PAYG plan data (static, no offer needed) ----------------------
// Rates below are the PAYG (2x) prices from the server constants documented
// above. Analysis (audio/video) per-use pricing is deliberately NOT listed.
export const PAYG_RATES: PricingCardRate[] = [
  {
    label: "Transcription",
    labelKey: "PRICING.RATES.TRANSCRIPTION",
    value: `$${PAYG_TRANSCRIPTION_RATE_PER_HOUR.toFixed(2)}/hr`,
  },
  {
    label: "Premium languages",
    labelKey: "PRICING.RATES.PREMIUM_LANGUAGES",
    value: "$3.00/hr",
    tooltip: "Languages transcribed on premium engines bill a higher hourly rate.",
    tooltipKey: "PRICING.RATES.PREMIUM_LANGUAGES_TIP",
  },
  {
    label: "Meeting transcription",
    labelKey: "PRICING.RATES.MEETINGS",
    value: "$4.00/hr",
  },
  {
    label: "Translation",
    labelKey: "PRICING.RATES.TRANSLATION",
    value: "$12.00/100K chars",
  },
  // One line only, no model names disclosed (same policy as the chat UI);
  // the tooltip explains that the per-chat price varies by model.
  {
    label: "AI Chat",
    labelKey: "PRICING.RATES.AI_CHAT",
    value: "from $0.08/chat",
    tooltip: "Priced per chat. It varies with the model you pick; figures are averages from real usage.",
    tooltipKey: "PRICING.RATES.AI_CHAT_TIP",
  },
];

// TODO: VATSAL REMOVE STATIC PRICING DATA
export const PAYG_FEATURES: string[] = [
  "100+ languages and diarization",
  "AI Meeting Assistant",
  "Web, iOS, and Android apps",
  "API, MCP server, and CLI",
  "SSO / SAML authentication",
  "Embeddable recorder and surveys",
  "Export transcripts (all formats)",
  "Webhooks and Zapier",
  "48-hour data access, then removed",
];

// -- Pro plan rates/features (static display) ----------------------

// TODO: VATSAL REMOVE STATIC PRICING DATA
export const PRO_RATES: PricingCardRate[] = [
  {
    label: "Transcription",
    value: "25 hrs included",
    tooltip:
      "Each seat includes 25 hours. Up to 10 of those hours can be used for AI Meeting Assistant.",
  },
  {
    label: "AI Chat",
    value: "Included",
    // Legacy (pre-credit) accounts only — these accounts still bill chat per
    // character, so the character framing stays accurate for them.
    tooltip:
      "Each Pro seat includes 1,250,000 characters per month. Beyond included characters, usage is billed at $2.00 per 250K characters. Credits reset monthly.",
  },
  { label: "Storage", value: "10 GB" },
];

// TODO: VATSAL REMOVE STATIC PRICING DATA
export const PRO_FEATURES: string[] = [
  "Unlimited data retention",
  "AI Meeting Assistant + auto-join",
  "AI Chat with smart model routing",
  "Team library (up to 5 users)",
  "Automations and workflows",
  "Library with folders and tags",
  "Branded recorder and surveys",
  "Usage analytics dashboard",
  "Priority email support",
];

// -- Custom plan rates/features (static display) -------------------

export const CUSTOM_RATES: PricingCardRate[] = [
  { label: "Transcription", value: "Volume rates" },
  { label: "AI Chat", value: "Custom" },
  { label: "Storage", value: "Unlimited" },
];

export const CUSTOM_FEATURES: PricingFeatureEntry[] = [
  // Multimodal launch lines lead the list, mirroring the public pricing page.
  {
    key: "PRICING.FEATURES.ANALYZE_TONE",
    text: "Analyze tone of voice",
    isNew: true,
  },
  {
    key: "PRICING.FEATURES.ANALYZE_VISUALS",
    text: "Analyze video and the visuals",
    isNew: true,
  },
  {
    key: "PRICING.FEATURES.CUSTOM_SCORING",
    text: "Custom scoring and coaching outputs",
  },
  {
    key: "PRICING.FEATURES.INTEGRATION_MIGRATION",
    text: "We integrate and migrate your data",
  },
  {
    key: "PRICING.FEATURES.HISTORICAL_DATA",
    text: "Primed with your historical data",
  },
  "Unlimited users and seats",
  "BAA, NDA, and custom agreements",
  "White-label and custom domains",
  "Dedicated account manager",
  "Data residency options",
  "Custom onboarding and training",
  "Custom integrations and API access",
  "Priority support with SLA",
];
